// 符號視覺 — 一顆符號（可跨 1~4 格的長條大符號）。
//
// 呈現策略（對照 Unity SymbolView）：
//   基底一律用靜態貼圖（SetNormal + SetNativeSize 等價），尺寸恆定；骨骼演出時暫時隱藏它、播完還原
//   （對應 Unity PlayAnimation 的 `_symbolImage.DOFade(0/1, 0)` + `_symbolSpine.SetActive(true/false)`）。
//   落定（land）／待機（loop）：見 playLand / playIdleLoop。Unity 是同一個 PlayAnimation 方法承擔兩者，
//     且 land 播完會自我重掛 loop（SymbolView.cs:222），故本移植也在 land 尾端接 loop。
//     只有 WW／H1／主遊戲 SS 的骨骼真的含 land，只有 WW／主遊戲 SS 含 loop；其餘符號無動畫即靜默不演
//     （對應 Unity `if (!_symbolSpine.HasAnimation(animationName)) return;`）。
//   中獎展示（PlayWinAnimation 等價）：隱藏靜態圖 → 疊符號骨骼播 `win`（loop）→ 時間到還原/接爆散。
//   骨骼縮放不量 getLocalBounds（setup 邊界同時含 1x1~1x4 變體、不可靠——實測見 2026-07-17 紀錄），
//   改用「骨骼單位＝Unity 設計像素」的既知比例：scale = cellW / 214，原點置於跨距中心（同靜態圖）。
//   消除爆散（PlayRemoveAnimation 等價）：隱藏靜態圖與木框，播共用 ani_remove 的 remove_1x{span}。
//   非中獎變暗（SetMask 等價）：本體＋木框 0.1s 漸變至 0.502 灰。
//
// 跨格（長條）呈現：本地原點 (0,0) 為「整顆符號跨距」的中心；貼圖底邊對齊跨距底邊
//   （1x1 大符號貼圖 214×192 略高於單格，會自然往上露出；`_1x{n}`(n>=2) 原生高度＝134×n，剛好填滿 n 格）。
//   格底木框（woodBg）只有恆 1 格的 7 個符號才有（依 Unity 的 BackgroundSprite 指派），可長條化的 6 個符號無框（已烘進貼圖）。

import { Container, Sprite, type DestroyOptions } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import { getScatterWolfTexture, getSymbolTexture, getWildVariantTexture } from '../assets/SymbolTextures'
import { getUiTexture } from '../assets/UiTextures'
import { createRemoveSpine, createSymbolSpine, pickSymbolAnimation, removeAnimationName } from '../assets/SymbolSpine'
import { woodBackgroundKeyFor } from '../config/symbols'
import {
  MASK_FADE_S,
  SYMBOL_MASK_TINT,
  SYMBOL_STATE_ANIM_MS,
  SYMBOL_STATE_MIX_S,
  WILD_CHANGE_STEP_MS,
  WILD_SYMBOL_ID,
  WIN_LOOP_MS,
} from '../constants'
import { delay } from '../../framework/core/async'
import { killTweensOf, tweenTo } from './tween'

/** 符號貼圖以 214 為基準寬度（來源所有符號貼圖寬皆 214），以 cellW/214 縮放即得正確跨格高度。 */
const SYMBOL_REF_WIDTH = 214
/** 消除爆散特效（ani_remove）相對格子的填充倍率（略大於格子，允許溢出，讓爆散明顯）。 */
const BURST_FILL_RATIO = 1.35
/** 無骨骼 win 動畫時的 fallback：本體放大彈跳倍率。 */
const WIN_FALLBACK_SCALE = 1.12

/** 由白到 _maskColor 的線性插值（t: 0=白, 1=灰）。 */
function maskTint(t: number): number {
  const gray = SYMBOL_MASK_TINT & 0xff
  const v = Math.round(255 + (gray - 255) * t)
  return (v << 16) | (v << 8) | v
}

export class SymbolView extends Container {
  /** 格底木框（僅恆 1 格符號顯示；可長條化符號隱藏）。 */
  private readonly background = new Sprite()
  /** 符號本體靜態貼圖（anchor 中心，置於跨距中心）。 */
  private readonly sprite = new Sprite()
  /** 中獎展示用符號骨骼（播 win loop 時掛上、結束銷毀）。 */
  private winSpine: Spine | null = null
  /** 落定（land）／待機（loop）用符號骨骼（對應 Unity 的 `_symbolSpine`；無對應動畫時不建立）。 */
  private stateSpine: Spine | null = null
  /** stateSpine 目前是否為待機 loop（用於避免對同一格重複重建 loop）。 */
  private stateLooping = false
  /** 消除爆散特效 Spine，播完即銷毀。 */
  private burst: Spine | null = null
  /** 變暗程度 tween 對象（0=正常、1=全灰）。 */
  private readonly maskProxy = { t: 0 }

  private symbolId = -1
  /** 顯示用符號 id（對應 Unity CellData.ViewSymbolId）：角色符號變身的 Wild 用此值選 W2/W3/W4 造型。 */
  private viewSymbolId = -1
  private span = 1
  private cellW = 0
  private cellH = 0
  private scatterLevel = 0

  constructor() {
    super()
    this.background.anchor.set(0.5)
    this.sprite.anchor.set(0.5)
    this.addChild(this.background, this.sprite)
  }

  /**
   * 呈現一顆符號（靜態貼圖）。span 為跨格高度（1~4）；cellW/cellH 為單格像素尺寸。
   * scatterLevel>0 時，Scatter 改用免費遊戲野狼分級造型（S1/S2/S3，對照 Unity 依 CellData.Level 選 Lv1/2/3Sprite）。
   * viewSymbolId 為顯示用原符號 id（省略＝symbolId）；當 symbolId 為 WILD 且 viewSymbolId 為角色符號（H2/H3/H4）時，
   * 顯示角色專屬百搭造型（W2/W3/W4）而非通用 WW（對照 Unity SymbolView 用 ViewSymbolId 查 SymbolData 取 WildSprite）。
   * 本地原點 (0,0) 為整顆跨距的中心，故此 SymbolView 應被放在「跨距中心」的螢幕座標。
   */
  show(symbolId: number, span: number, cellW: number, cellH: number, scatterLevel = 0, viewSymbolId = symbolId): void {
    this.symbolId = symbolId
    this.viewSymbolId = viewSymbolId
    this.span = Math.max(1, span)
    this.cellW = cellW
    this.cellH = cellH
    this.scatterLevel = scatterLevel

    this.disposeBurst()
    this.disposeWinSpine()
    this.disposeStateSpine()
    killTweensOf([this.sprite.scale, this.sprite, this.maskProxy, this])
    this.alpha = 1
    this.sprite.visible = true
    this.sprite.scale.set(1)
    this.sprite.rotation = 0
    this.setDimmedImmediate(false)

    this.applyBackground(symbolId, this.span, cellW, cellH)
    this.applySprite(symbolId, this.span, cellW, scatterLevel, viewSymbolId)
  }

  /** 是否為「角色符號變身的 Wild」（symbolId=WILD 但 viewSymbolId 指向角色符號 H2/H3/H4）。 */
  private isCharacterWild(): boolean {
    return this.symbolId === WILD_SYMBOL_ID && this.viewSymbolId !== this.symbolId && getWildVariantTexture(this.viewSymbolId, this.span) !== null
  }

  /**
   * 格底木框：恆 1 格符號顯示對應階級 woodBg，撐滿整個跨距（span 格高、置中）；可長條化符號隱藏。
   * span 幾乎恆為 1，但後端可能對「無長條美術的符號」（H5/H6/H7/LA/LK/LQ/LJ）給 Size>=2，
   * 此時木框需撐成 span 格高填滿跨距，否則符號會浮在跨距中心、上下露出盤面木紋而看似「中間空格」。
   * 對照 Unity SymbolView 的 `_backgroundImage.rectTransform.sizeDelta = (x, y * Size)`。
   */
  private applyBackground(symbolId: number, span: number, cellW: number, cellH: number): void {
    const key = woodBackgroundKeyFor(symbolId)
    if (key === null) {
      this.background.visible = false
      return
    }
    this.background.visible = true
    this.background.texture = getUiTexture(key)
    this.background.width = cellW
    this.background.height = cellH * span
    this.background.position.set(0, 0)
  }

  /**
   * 符號本體：貼圖以 cellW/214 縮放（自動得跨 span 格的高度），置中於跨距中心（本地原點）。
   * scatterLevel>0 時改用野狼分級貼圖（S1/S2/S3，同為 214 寬、尺寸感知），野狼圖未載入時回退一般 SS。
   * 角色 Wild（symbolId=WILD 且 viewSymbolId 為角色符號）改用 W2/W3/W4 造型，未載入時回退通用 WW。
   */
  private applySprite(symbolId: number, span: number, cellW: number, scatterLevel: number, viewSymbolId: number): void {
    const texture =
      scatterLevel > 0
        ? (getScatterWolfTexture(scatterLevel, span) ?? getSymbolTexture(symbolId, span))
        : symbolId === WILD_SYMBOL_ID && viewSymbolId !== symbolId
          ? (getWildVariantTexture(viewSymbolId, span) ?? getSymbolTexture(symbolId, span))
          : getSymbolTexture(symbolId, span)
    this.sprite.texture = texture
    const scale = cellW / SYMBOL_REF_WIDTH
    this.sprite.scale.set(scale)
    // span>=2 的貼圖原生高度＝134×span，縮放後剛好＝span 格高、置中即填滿跨距；
    // 1x1 大符號貼圖(214×192)略高於單格，置中時上下各略微露出（對照 Unity SetNativeSize + 中心 pivot）。
    this.sprite.position.set(0, 0)
  }

  /**
   * 非中獎符號變暗／還原（對應 Unity SetMask 的 DOColor 至 0.502 灰，0.1s）。
   * 對本體貼圖與木框同時漸變；win/remove 骨骼不變暗（中獎格不會被 mask）。
   */
  setDimmed(dimmed: boolean): void {
    killTweensOf(this.maskProxy)
    void tweenTo(this.maskProxy, {
      t: dimmed ? 1 : 0,
      duration: MASK_FADE_S,
      ease: 'none',
      onUpdate: () => this.applyMaskTint(),
    })
  }

  /** 立即設定變暗狀態（不漸變），供重繪/回收時歸零。 */
  private setDimmedImmediate(dimmed: boolean): void {
    this.maskProxy.t = dimmed ? 1 : 0
    this.applyMaskTint()
  }

  private applyMaskTint(): void {
    const tint = maskTint(this.maskProxy.t)
    this.sprite.tint = tint
    this.background.tint = tint
    // Unity SetMask 對 `_symbolSpine` 也套同一份 DOColor，故落定/待機骨骼要跟著變暗
    //（否則非中獎的 WW/SS 在消除演出期間會是唯一沒暗下來的格）。Spine 吃 Container 的 groupColor。
    if (this.stateSpine !== null && !this.stateSpine.destroyed) this.stateSpine.tint = tint
  }

  /**
   * 落定演出（對應 Unity ReelStrip.DoFillEffect 尾端的 `_playEffectHandle(symbolView, "land", 1.0f, false)`）：
   * 播一次 `land` 骨骼，播完接上待機 loop（對應 Unity PlayAnimation 尾端的自我重掛，SymbolView.cs:222）。
   * 只有 WW／H1／主遊戲 SS 的骨骼含 land，其餘符號無動畫即靜默不演。不 await（落定是逐顆並行的，不可阻塞補位流程）。
   */
  playLand(): void {
    const spine = this.beginStateSpine('land', false)
    if (spine === null) return
    const loopAnimation = pickSymbolAnimation(spine, 'loop', this.symbolId, this.span, this.scatterLevel)
    if (loopAnimation !== null) {
      // 有待機 loop（WW／主遊戲 SS）：於同一骨骼實例以 addAnimation 排隊，land 播完由 Spine 原生在結束前
      // SYMBOL_STATE_MIX_S 秒交叉淡化接上 loop（見 defaultMix），取代「dispose 舊骨骼＋新建 loop」的瞬切＋計時
      // 不同步。stateLooping 先行設 true，避免 startIdleLoops 在 land 期間打斷此序列。
      spine.state.addAnimation(0, loopAnimation, true, 0)
      this.stateLooping = true
      return
    }
    // 無待機 loop（H1）：land 播完還原靜態貼圖。以骨骼實際長度計時、於更新迴圈外收尾
    //（不可在 Spine complete 事件內銷毀骨骼——那會在 state.apply 之前 destroy skeleton 而崩潰）。
    const durationMs = (spine.state.tracks[0]?.animation?.duration ?? SYMBOL_STATE_ANIM_MS / 1000) * 1000
    void delay(durationMs).then(() => {
      // 期間可能已被消除演出／重繪／resize 換掉骨骼，故以身分比對確認這支仍是當前的才收尾。
      if (this.destroyed || this.stateSpine !== spine) return
      this.disposeStateSpine()
      this.sprite.visible = true
    })
  }

  /**
   * 待機演出（對應 Unity SymbolView.PlayLoopAnimation → `PlayAnimation("loop", 1.0f, true)`）：
   * 只有 WW／主遊戲 SS 的骨骼含 loop，其餘符號（含只有 land 的 H1）無動畫即維持靜態貼圖。
   * Unity 是每 1.0s 以 SetAnimation 重掛同一支 loop（實測 loop 長度恰為 1.0s，故重掛在視覺上無縫）；
   * 本移植直接以 loop=true 連續播放，視覺等價且不必每秒重建。已在待機中（含 land→loop 排隊中）則不重建。
   */
  playIdleLoop(): void {
    if (this.stateLooping) return
    if (this.beginStateSpine('loop', true) !== null) this.stateLooping = true
  }

  /**
   * 建立並掛上落定/待機骨骼、播放 base 動畫（loop 決定是否循環），回傳掛好的 Spine。
   * 無可播動畫（或此格不該演出）時回傳 null 並不動任何狀態。stateLooping 由呼叫端依情境設定。
   * 對應 Unity PlayAnimation 的前半段：`HasAnimation` 閘門 → 隱藏靜態圖 → 啟用骨骼 → SetAnimation。
   */
  private beginStateSpine(base: string, loop: boolean): Spine | null {
    // 中獎/爆散演出優先，期間不疊落定/待機骨骼（Unity 亦以同一個 _symbolSpine 承載，天然互斥）。
    if (this.winSpine !== null || this.burst !== null) return null
    // 角色 Wild（W2/W3/W4）不可套通用 WW 骨骼（會顯示成 WW），同 playWinLoop 的既有理由。
    if (this.isCharacterWild()) return null

    const spine = createSymbolSpine(this.symbolId)
    if (spine === null) return null
    const animation = pickSymbolAnimation(spine, base, this.symbolId, this.span, this.scatterLevel)
    if (animation === null) {
      if (!spine.destroyed) spine.destroy()
      return null
    }

    this.disposeStateSpine()
    // 動畫之間留一小段 mix，讓 land→loop 交叉淡化而非瞬切（見 playLand 的 addAnimation）。
    spine.state.data.defaultMix = SYMBOL_STATE_MIX_S
    spine.state.setAnimation(0, animation, loop)
    spine.scale.set(this.cellW / SYMBOL_REF_WIDTH)
    spine.position.set(0, 0)
    // 立即套用動畫第一幀：新建骨骼在首次 ticker 更新前若被 render，會先顯示 setup pose（綁定姿勢）
    // 一整幀而「閃一下」；先 update(0) 讓掛上當幀即為正確姿勢。
    spine.update(0)
    this.stateSpine = spine
    this.stateLooping = false
    this.addChild(spine)
    this.sprite.visible = false
    this.applyMaskTint()
    return spine
  }

  /**
   * 中獎展示：隱藏靜態圖、疊符號骨骼播 `win`（loop）holdMs 毫秒（對應 Unity PlayWinAnimation）。
   * restoreAfter=true（Scatter 展示等「不消除」情境）時結束還原靜態圖；false 時維持隱藏（接爆散）。
   * 骨骼不可用或無對應動畫時 fallback：本體放大彈跳並停留同樣時長。
   */
  async playWinLoop(holdMs: number, restoreAfter: boolean): Promise<void> {
    this.disposeWinSpine()
    this.disposeStateSpine()
    // 角色 Wild（W2/W3/W4）沒有專屬 win 骨骼、且不可套用通用 WW 骨骼（會顯示成 WW），改走靜態貼圖彈跳
    // fallback（維持角色百搭造型）。其餘符號正常用各自骨骼。
    const spine = this.isCharacterWild() ? null : createSymbolSpine(this.symbolId)
    const animation = spine !== null ? pickSymbolAnimation(spine, 'win', this.symbolId, this.span, this.scatterLevel) : null

    if (spine !== null && animation !== null) {
      spine.state.setAnimation(0, animation, true)
      spine.scale.set(this.cellW / SYMBOL_REF_WIDTH)
      spine.position.set(0, 0)
      // 立即套第一幀，避免新建骨骼在首次 ticker 更新前被 render 而閃一幀 setup pose。
      spine.update(0)
      this.winSpine = spine
      this.addChild(spine)
      this.sprite.visible = false
      await delay(holdMs)
      if (this.destroyed) return
      this.disposeWinSpine()
      if (restoreAfter) {
        this.sprite.visible = true
        // Unity PlayAnimation 尾端一律重掛待機 loop（SymbolView.cs:222）；無 loop 動畫的符號此呼叫是 no-op。
        this.playIdleLoop()
      }
    } else {
      // fallback：無骨骼時本體放大彈跳強調，停留同樣時長。
      if (spine !== null && !spine.destroyed) spine.destroy()
      killTweensOf(this.sprite.scale)
      const base = this.cellW / SYMBOL_REF_WIDTH
      void tweenTo(this.sprite.scale, { x: base * WIN_FALLBACK_SCALE, y: base * WIN_FALLBACK_SCALE, duration: 0.18, ease: 'back.out(2)' })
      await delay(holdMs)
      if (this.destroyed) return
      killTweensOf(this.sprite.scale)
      this.sprite.scale.set(base)
      if (!restoreAfter) this.sprite.visible = true // 交由 playRemoveBurst 隱藏
    }
  }

  /**
   * 消除爆散（對應 Unity PlayRemoveAnimation）：隱藏靜態圖與木框，播 remove_1x{span}（實際動畫長度），
   * 結束後本格整體隱藏（交由後續補位接手）。
   */
  async playRemoveBurst(): Promise<void> {
    this.disposeStateSpine()
    this.sprite.visible = false
    this.background.visible = false

    const burst = createRemoveSpine()
    this.fitBurst(burst)
    this.addChild(burst)
    this.burst = burst

    const animationName = removeAnimationName(this.span)
    const animation = burst.skeleton.data.findAnimation(animationName)
    const burstDurationMs = Math.max(0.3, animation?.duration ?? 0.6) * 1000
    if (animation !== null) {
      burst.state.setAnimation(0, animationName, false)
      // 立即套第一幀，避免新建骨骼在首次 ticker 更新前被 render 而閃一幀 setup pose。
      burst.update(0)
    }

    await delay(burstDurationMs)
    this.disposeBurst()
    this.alpha = 0
  }

  /** 中獎格完整消除序列：win loop（1.5s）→ 爆散。對應 Unity PlayWinAnimation(playRemove:true)。 */
  async playEliminate(): Promise<void> {
    await this.playWinLoop(WIN_LOOP_MS, false)
    await this.playRemoveBurst()
  }

  /**
   * Wild 變身（對應 Unity ReelController.PlayChangeAnimation）：此時本體顯示的是「變身前原符號」
   * （fromSymbol，由 ReelsView 補牌時以顯示覆寫落下）。序列：
   *   原符號骨骼 win（固定 1.0s）→ 原符號骨骼 change（固定 1.0s，僅 H2/H3/H4 有此動畫）
   *   → 換成「Wild + 原符號造型」本體：角色符號（H2/H3/H4）顯示角色專屬百搭 W2/W3/W4、其餘顯示通用 WW
   *   （對照 Unity SetWild 只改 SymbolId、ViewSymbolId 維持原符號 → SymbolView 用 ViewSymbolId 選 WildSprite）。
   * 骨骼無 change 動畫時 fallback：縮小翻面 → 換造型 → 彈回。
   */
  async playWildTransform(): Promise<void> {
    // 變身前本體顯示的是原符號（fromSymbol）；變身後 symbolId 變 WILD，但 viewSymbolId 保留 fromSymbol。
    const fromSymbol = this.symbolId
    await this.playWinLoop(WILD_CHANGE_STEP_MS, true)
    if (this.destroyed) return
    const morphed = await this.playChangeSpine()
    if (this.destroyed) return
    if (morphed) {
      // change 動畫已演完整段變身，換成「Wild + 原符號造型」本體（viewSymbolId=fromSymbol → W2/W3/W4 或 WW）。
      this.show(WILD_SYMBOL_ID, this.span, this.cellW, this.cellH, 0, fromSymbol)
      return
    }
    // fallback：縮小翻面 → 換造型 → 彈回（無 change 骨骼動畫的符號）。
    killTweensOf([this.sprite.scale, this.sprite])
    const base = this.sprite.scale.x
    await tweenTo(this.sprite.scale, { x: base * 0.12, y: base, duration: 0.16, ease: 'power2.in' })
    if (this.destroyed) return
    this.show(WILD_SYMBOL_ID, this.span, this.cellW, this.cellH, 0, fromSymbol)
    this.sprite.scale.set(base * 0.12, base)
    await tweenTo(this.sprite.scale, { x: base, y: base, duration: 0.26, ease: 'back.out(2.2)' })
  }

  /**
   * 播原符號骨骼的 change 變身動畫一次並固定等待 1.0s（對照 Unity PlayAnimation("change", 1.0f)）。
   * @returns 是否有 change 動畫可播（false＝呼叫端走 fallback）。
   */
  private async playChangeSpine(): Promise<boolean> {
    const spine = createSymbolSpine(this.symbolId)
    const animation = spine !== null ? pickSymbolAnimation(spine, 'change', this.symbolId, this.span, this.scatterLevel) : null
    if (spine === null || animation === null) {
      if (spine !== null && !spine.destroyed) spine.destroy()
      return false
    }
    this.disposeWinSpine()
    this.disposeStateSpine()
    spine.state.setAnimation(0, animation, false)
    spine.scale.set(this.cellW / SYMBOL_REF_WIDTH)
    spine.position.set(0, 0)
    // 立即套第一幀，避免新建骨骼在首次 ticker 更新前被 render 而閃一幀 setup pose。
    spine.update(0)
    this.winSpine = spine
    this.addChild(spine)
    this.sprite.visible = false
    await delay(WILD_CHANGE_STEP_MS)
    if (this.destroyed) return true
    this.disposeWinSpine()
    this.sprite.visible = true
    return true
  }

  /** 把爆散特效縮放置中到跨距上（依 setup 邊界換算，允許略大於格子溢出）。 */
  private fitBurst(burst: Spine): void {
    burst.scale.set(1)
    const bounds = burst.getLocalBounds()
    const maxDim = Math.max(bounds.width, bounds.height)
    const spanHeight = this.span * this.cellH
    const target = Math.max(this.cellW, spanHeight) * BURST_FILL_RATIO
    const scale = maxDim > 1 ? target / maxDim : this.cellW / 160
    burst.scale.set(scale)
    const cx = bounds.x + bounds.width / 2
    const cy = bounds.y + bounds.height / 2
    burst.position.set(-cx * scale, -cy * scale)
  }

  private disposeBurst(): void {
    if (this.burst === null) return
    if (!this.burst.destroyed) {
      this.removeChild(this.burst)
      this.burst.destroy()
    }
    this.burst = null
  }

  private disposeWinSpine(): void {
    if (this.winSpine === null) return
    if (!this.winSpine.destroyed) {
      this.removeChild(this.winSpine)
      this.winSpine.destroy()
    }
    this.winSpine = null
  }

  private disposeStateSpine(): void {
    this.stateLooping = false
    if (this.stateSpine === null) return
    if (!this.stateSpine.destroyed) {
      this.removeChild(this.stateSpine)
      this.stateSpine.destroy()
    }
    this.stateSpine = null
  }

  /**
   * background 與 sprite 的 texture 皆為全域共用快取，其他 SymbolView 仍在使用，
   * 故只釋放 Sprite 本身、不連 texture 一起銷毀；Spine 實例則需銷毀。
   */
  override destroy(options?: DestroyOptions): void {
    killTweensOf([this.sprite.scale, this.sprite, this.maskProxy, this])
    this.disposeBurst()
    this.disposeWinSpine()
    this.disposeStateSpine()
    this.background.destroy()
    this.sprite.destroy()
    super.destroy(options)
  }
}
